import React, { createContext, useContext } from "react";

import type { CoachSelect } from "~/../drizzle/schema";
import type { CoachPrivateData, CoachPublicData } from "~/shared/loaders";

type CoachContextType = CoachSelect | null;
type CoachPublicProfileContextType = CoachPublicData;
type CoachPrivateProfileContextType = CoachPrivateData;

const CoachContext = createContext<CoachContextType>(null);
const CoachPublicProfileContext =
  createContext<CoachPublicProfileContextType | null>(null);
const CoachPrivateProfileContext =
  createContext<CoachPrivateProfileContextType | null>(null);

export const useCoach = () => {
  const context = useContext(CoachContext);
  return context;
};

export const useCoachProfile = () => {
  const context = useContext(CoachPublicProfileContext);
  return context;
};

export const useCoachPrivateProfile = () => {
  const context = useContext(CoachPrivateProfileContext);
  return context;
};

/**
 * Hook for backend routes where coach private data is guaranteed to be available
 * This will throw a compile-time error if coach data is null, ensuring type safety
 */
export const useCoachPrivateProfileRequired = (): CoachPrivateData => {
  const context = useContext(CoachPrivateProfileContext);

  if (!context) {
    throw new Error(
      "useCoachPrivateProfileRequired must be used within a CoachPrivateProfileProvider. " +
        "This indicates the backend route is not properly configured to load coach data.",
    );
  }

  return context;
};

export const CoachProvider: React.FC<{
  children: React.ReactNode;
  coach: CoachSelect;
}> = ({ children, coach }) => {
  return (
    <CoachContext.Provider value={coach}>{children}</CoachContext.Provider>
  );
};

export const CoachPublicProfileProvider: React.FC<{
  children: React.ReactNode;
  coachProfile: CoachPublicData;
}> = ({ children, coachProfile }) => {
  return (
    <CoachPublicProfileContext.Provider value={coachProfile}>
      {children}
    </CoachPublicProfileContext.Provider>
  );
};

export const CoachPrivateProfileProvider: React.FC<{
  children: React.ReactNode;
  coachProfile: CoachPrivateData;
}> = ({ children, coachProfile }) => {
  return (
    <CoachPrivateProfileContext.Provider value={coachProfile}>
      {children}
    </CoachPrivateProfileContext.Provider>
  );
};

export { CoachContext, CoachPublicProfileContext, CoachPrivateProfileContext };
